import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import BarcodeInput from "../components/BarcodeInput";
import CameraScanner from "../components/CameraScanner";
import { toast } from "sonner";
import { ArrowRightLeft, Camera, Search } from "lucide-react";

/**
 * Sehpalar Arası Transfer
 * Kullanıcı barkod okutur / yazar, hedef sehpayı seçer, Transfer'a basar.
 * Backend barkodun sehpa_konumu'nu günceller ve hareket geçmişine "transfer" olarak yazar.
 */
export default function Transfer() {
  const [barkod, setBarkod] = useState("");
  const [hedef, setHedef] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [preview, setPreview] = useState(null); // {barkod, isim, ebat, sehpa_konumu, adet}
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [shelves, setShelves] = useState([]);

  useEffect(() => {
    api.get("/shelves").then(({ data }) => setShelves(data)).catch(() => {});
  }, []);

  const lookup = async (raw) => {
    const b = String(raw).trim();
    if (!b) return { ok: false };
    setPreviewLoading(true);
    setPreview(null);
    setBarkod(b);
    try {
      const { data } = await api.get(`/inventory/by-barcode/${encodeURIComponent(b)}`);
      if (!data.found) {
        toast.error(`Barkod bulunamadı: ${b}`);
        return { ok: false };
      }
      setPreview(data.item);
      return { ok: true };
    } catch (e) {
      toast.error(formatApiError(e));
      return { ok: false };
    } finally {
      setPreviewLoading(false);
    }
  };

  const submit = async () => {
    if (!preview) {
      toast.error("Önce barkod okutun.");
      return;
    }
    if (!hedef.trim()) {
      toast.error("Hedef sehpa boş olamaz.");
      return;
    }
    const hedefNorm = hedef.trim().toUpperCase();
    if (hedefNorm === (preview.sehpa_konumu || "").toUpperCase()) {
      toast.error("Kaynak ve hedef sehpa aynı.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/transfer", {
        barkod: preview.barkod,
        hedef_sehpa: hedefNorm,
        aciklama,
      });
      toast.success(
        `Transfer OK · ${data.barkod} · ${data.kaynak_sehpa} → ${data.hedef_sehpa}`,
      );
      setBarkod("");
      setPreview(null);
      setAciklama("");
      // keep hedef so multiple transfers to same shelf are quick
      // reload shelves list in case a new shelf was created
      api.get("/shelves").then(({ data }) => setShelves(data)).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5">
        <div className="ticker text-[color:var(--text-3)] mb-2">
          /// SEHPALAR ARASI TRANSFER
        </div>
        <p className="text-sm text-[color:var(--text-2)]">
          Barkodu okutun veya elle yazın. Sistem mevcut sehpayı gösterecektir.
          Hedef sehpayı seçip Transfer&apos;e basın.
        </p>
      </div>

      {/* Barcode lookup */}
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="ticker text-[color:var(--text-3)]">/// 1. BARKOD</div>
          <button
            data-testid="transfer-camera-btn"
            onClick={() => setCameraOpen(true)}
            className="flex items-center gap-2 px-3 py-2 hard border-2 border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors text-sm font-semibold"
          >
            <Camera size={16} /> Kamera
          </button>
        </div>
        <BarcodeInput
          onScan={lookup}
          disabled={submitting}
          placeholder="Barkodu okutun veya yazıp Enter'a basın"
          testId="transfer-barcode-input"
        />

        {previewLoading && (
          <div className="ticker text-[color:var(--text-3)]">Aranıyor…</div>
        )}

        {preview && (
          <div
            data-testid="transfer-preview"
            className="hard border-2 border-[color:var(--primary)] bg-[color:var(--bg)] p-4"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="ticker text-[color:var(--text-3)]">Barkod</div>
                <div className="font-mono font-bold">{preview.barkod}</div>
              </div>
              <div>
                <div className="ticker text-[color:var(--text-3)]">İsim</div>
                <div className="truncate">{preview.isim || "-"}</div>
              </div>
              <div>
                <div className="ticker text-[color:var(--text-3)]">Ebat</div>
                <div className="font-mono">{preview.ebat || "-"}</div>
              </div>
              <div>
                <div className="ticker text-[color:var(--text-3)]">Adet</div>
                <div className="font-mono font-bold">{preview.adet}</div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-[color:var(--border)] flex items-center gap-3">
              <div className="ticker text-[color:var(--text-3)]">Mevcut Sehpa</div>
              <span className="inline-block px-3 py-1 hard bg-[color:var(--primary)] text-white font-mono font-bold">
                {preview.sehpa_konumu || "-"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Target shelf */}
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5 space-y-4">
        <div className="ticker text-[color:var(--text-3)]">/// 2. HEDEF SEHPA</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="ticker block mb-2 text-[color:var(--text-2)]">
              Hedef Sehpa <span className="text-[color:var(--error)]">*</span>
            </label>
            <input
              data-testid="transfer-hedef-input"
              list="transfer-shelves-list"
              value={hedef}
              onChange={(e) => setHedef(e.target.value.toUpperCase())}
              placeholder="Örn: B-3, AX-14"
              disabled={submitting || !preview}
              className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--accent)] outline-none font-mono font-bold disabled:opacity-50"
            />
            <datalist id="transfer-shelves-list">
              {shelves.map((s) => (
                <option key={s.sehpa} value={s.sehpa} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="ticker block mb-2 text-[color:var(--text-2)]">
              Açıklama
            </label>
            <input
              data-testid="transfer-note"
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
              placeholder="Opsiyonel not"
              disabled={submitting || !preview}
              className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--accent)] outline-none disabled:opacity-50"
            />
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {preview && hedef ? (
            <>
              <span className="inline-block px-3 py-1 hard bg-[color:var(--bg)] font-mono font-bold border border-[color:var(--border)]">
                {preview.sehpa_konumu || "-"}
              </span>
              <ArrowRightLeft size={20} className="text-[color:var(--accent)]" />
              <span className="inline-block px-3 py-1 hard bg-[color:var(--accent)] text-white font-mono font-bold">
                {hedef.trim().toUpperCase() || "-"}
              </span>
            </>
          ) : (
            <div className="ticker text-[color:var(--text-3)]">
              Barkod ve hedef seçilince özet burada görünür
            </div>
          )}
        </div>
        <button
          data-testid="transfer-submit"
          onClick={submit}
          disabled={submitting || !preview || !hedef.trim()}
          className="px-6 h-11 hard bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white font-bold uppercase tracking-wider flex items-center gap-2 disabled:opacity-40 transition-colors"
        >
          <ArrowRightLeft size={16} strokeWidth={2.5} />
          {submitting ? "Aktarılıyor…" : "Transfer Et"}
        </button>
      </div>

      <CameraScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onScan={(code) => {
          lookup(code);
          setCameraOpen(false);
        }}
      />
    </div>
  );
}
